// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns
import { useState } from 'react';

import { useLazyQueryPipeline, useGetPipeline } from '@ion/api';
import isFullyDeployed from '@ion/api/pipelines/utils/determinePipelineStatus/isFullyDeployed';

import useLazyQueryPipelineIds from '../useLazyQueryPipelineIds';
import { useConcurrentMap, BULK_DEPLOY_CONCURRENCY } from 'ion-experimental/api/concurrency';
import { withTransientRetry } from 'ion-experimental/api/retry';

/**
 *  Tech Debt :: SP :: This behavior is intended to move to Action Proxy and should ideally not live long
 *
 *  Return Signature :: Array Of ->
 *  Deployable Pipeline
 *    id: uuid! -- Staged Pipeline ID
 *    name: String -- Pipeline Name
 *    writeKey: String
 *    cluster: {
 *      id: uuid!
 *      name: String!
 *    }
 *    status: "Staged for Deploy" | "Error"
 *    unmanagedIntegrationsStatus: "NONE" | "SOME"
 *    lastDeployed: String -- ISO date string, null if never deployed
 *    integrations: Array<{
 *      id: uuid!
 *      name: String!
 *      starterkitId: String!
 *    }>
 */
export default function useGetDeployablePipelines() {
  // Data State
  const [deployablePipelines, setDeployablePipelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data Hooks
  const [getPipelineIds] = useLazyQueryPipelineIds();
  const [getStagedPipeline] = useLazyQueryPipeline();
  const [getDeployedPipeline] = useGetPipeline();
  const [runConcurrent, progress] = useConcurrentMap();

  const getDeployablePipelines = async ({ clusterIds, integrationId, playbookId, tagIds, searchTerm = '' }) => {
    setLoading(true);

    try {
      let updatedDeployablePipelineList = [];
      // Refetch all pipeline IDs based on filters
      const pipelineIdResponse = await getPipelineIds({ clusterIds, integrationId, playbookId, tagIds, searchTerm });
      if (pipelineIdResponse.errors) {
        throw new Error(pipelineIdResponse.errors.message);
      }

      const pipelineIds = pipelineIdResponse.data.pipelines.map(pipeline => pipeline.id);

      // Iterate over all pipelines to determine which are "deployable", capping the
      // concurrent staged + deployed fetches so a large client doesn't swamp the cluster
      await runConcurrent(pipelineIds, BULK_DEPLOY_CONCURRENCY, async pipelineId => {
        // Get the full staged pipeline
        let stagedPipeline;
        try {
          const pipelineResponse = await getStagedPipeline({ variables: { pipelineId } });

          stagedPipeline = pipelineResponse.data.pipelines_by_pk;
        } catch (error) {
          // If we fail to get the staged pipeline stash it and move on
          const deployablePipeline = buildDeployablePipeline({ id: pipelineId, status: 'Error' });
          updatedDeployablePipelineList.push(deployablePipeline);

          return;
        }

        // If the pipeline has no integrations it can be ignored
        if (!stagedPipeline.pipelineIntegrations.length) {
          return;
        }

        // Get the deployed pipeline
        let deployedPipeline;
        try {
          const deployedPipelineResponse = await withTransientRetry(() =>
            getDeployedPipeline({
              clusterId: stagedPipeline.clusterId,
              writekey: stagedPipeline.writekey,
            })
          );

          const pipelineNotDeployed = deployedPipelineResponse.errors?.message.includes('404');
          if (pipelineNotDeployed) {
            // If the pipeline is not deployed store it and move on
            const deployablePipeline = buildDeployablePipeline({
              id: pipelineId,
              name: stagedPipeline.name,
              writeKey: stagedPipeline.writekey,
              cluster: stagedPipeline.cluster,
              status: 'Staged for Deploy',
              unmanagedIntegrationsStatus: 'NONE',
              integrations: stagedPipeline.pipelineIntegrations.map(
                pipelineIntegration => pipelineIntegration.integration
              ),
              pipelineTags: stagedPipeline.pipelineTags,
            });
            updatedDeployablePipelineList.push(deployablePipeline);

            return;
          } else if (deployedPipelineResponse.errors?.message.length) {
            // If the deployed pipeline request ran into unexpected error stash the pipeline and move on
            const deployablePipeline = buildDeployablePipeline({
              id: pipelineId,
              name: stagedPipeline.name,
              writeKey: stagedPipeline.writekey,
              cluster: stagedPipeline.cluster,
              status: 'Error',
              integrations: stagedPipeline.pipelineIntegrations.map(
                pipelineIntegration => pipelineIntegration.integration
              ),
              pipelineTags: stagedPipeline.pipelineTags,
            });
            updatedDeployablePipelineList.push(deployablePipeline);

            return;
          }

          deployedPipeline = deployedPipelineResponse.pipeline;
        } catch (error) {
          // Catpure the deployable pipeline if the cluster request failed
          const deployablePipeline = buildDeployablePipeline({
            id: pipelineId,
            status: 'Error',
            writeKey: stagedPipeline.writekey,
            cluster: stagedPipeline.cluster,
            integrations: stagedPipeline.pipelineIntegrations.map(
              pipelineIntegration => pipelineIntegration.integration
            ),
            pipelineTags: stagedPipeline.pipelineTags,
          });
          updatedDeployablePipelineList.push(deployablePipeline);

          return;
        }

        const pipelineFullyDeployed = determineIsFullyDeployed(stagedPipeline, deployedPipeline);
        if (pipelineFullyDeployed) {
          // If the pipeline is full deployed move on
          return;
        }

        const hasUnmanagedIntegrations = containsUnmanagedIntegrations(
          stagedPipeline.pipelineIntegrations,
          deployedPipeline.integrations
        );
        const deployablePipeline = buildDeployablePipeline({
          id: pipelineId,
          name: stagedPipeline.name,
          writeKey: stagedPipeline.writekey,
          cluster: stagedPipeline.cluster,
          status: 'Staged for Deploy',
          unmanagedIntegrationsStatus: hasUnmanagedIntegrations ? 'SOME' : 'NONE',
          integrations: stagedPipeline.pipelineIntegrations.map(pipelineIntegration => pipelineIntegration.integration),
          pipelineTags: stagedPipeline.pipelineTags,
          lastDeployed: deployedPipeline.createdAt,
        });
        updatedDeployablePipelineList.push(deployablePipeline);
      });

      setDeployablePipelines(updatedDeployablePipelineList);
      return {
        data: {
          get_deployable_pipelines: {
            pipelines: updatedDeployablePipelineList,
            totalCount: updatedDeployablePipelineList.length,
          },
        },
      };
    } catch (error) {
      console.error(error);
      setError(error);
      setDeployablePipelines([]);
    } finally {
      setLoading(false);
    }
  };

  return [getDeployablePipelines, { deployablePipelines, loading, error, progress }];
}

function buildDeployablePipeline(options) {
  const { id, name, writeKey, cluster, status, unmanagedIntegrationsStatus, lastDeployed, integrations, pipelineTags } =
    options;

  return {
    id,
    name,
    writeKey,
    cluster,
    status,
    unmanagedIntegrationsStatus,
    lastDeployed,
    integrations,
    pipelineTags,
  };
}

// Map the staged pipeline to the shape isFullyDeployed expects and compare
function determineIsFullyDeployed(stagedPipeline, deployedPipeline) {
  const transformedStagedPipeline = {
    ...stagedPipeline,
    pipelineIntegrations: stagedPipeline.pipelineIntegrations.map(pipelineIntegration => ({
      ...pipelineIntegration,
      consent: {
        ...pipelineIntegration.pipelineIntegrationConsent,
        category: {
          ...pipelineIntegration.pipelineIntegrationConsent?.consentCategory,
        },
      },
      revision: {
        ...pipelineIntegration.stagedRevision,
        playbook: {
          ...pipelineIntegration.stagedRevision.playbook,
          pipelineVars: pipelineIntegration.stagedRevision.playbook.pipeline_variables,
        },
      },
    })),
  };

  return isFullyDeployed(transformedStagedPipeline, { pipeline: deployedPipeline });
}

function containsUnmanagedIntegrations(stagedIntegrations, deployedIntegrations) {
  const stagedIntegrationIds = stagedIntegrations.map(pipelineIntegration => pipelineIntegration.integration.id);
  const deployedIntegrationIds = deployedIntegrations.map(integration => integration.id);

  /**
   * If the deployed pipeline contains any integration IDs not represented in the staged pipeline
   *  we know that there are CLI-managed integrations attached to this pipeline on the cluster
   */
  return !deployedIntegrationIds.every(integrationId => stagedIntegrationIds.includes(integrationId));
}
